class ProductsSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.abortController = null;
    this.cancelVisibility = null;
  }

  connectedCallback() {
    if (this.dataset.emptyInitial === 'true') {
      this.hideParentSectionIfEmpty();
      return;
    }

    // Swiper measures every slide on init, which is a forced layout. Hold that
    // off until the slider is near the viewport; above-the-fold sliders still
    // initialize on the observer's first callback.
    this.cancelVisibility = window.wakaWhenVisible(this, () => {
      this.cancelVisibility = null;

      if (this.dataset.source === 'recommendations') {
        this.loadRecommendations();
      } else if (this.dataset.source === 'recently_viewed') {
        this.loadRecentlyViewed();
      } else {
        this.ensureSwiper();
      }
    });
  }

  /**
   * Idempotent init, for callers that may race the visibility-gated init
   * above (the tabs wrapper). Use initSwiper directly after replacing slides.
   */
  ensureSwiper() {
    if (this.swiper) return;
    this.initSwiper();
  }

  /**
   * Swiper measures every slide, so keep that out of the frame that just
   * inserted them — otherwise the measurement forces a synchronous layout.
   */
  initSwiperOnNextFrame() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.initSwiper();
    });
  }

  disconnectedCallback() {
    if (this.cancelVisibility) {
      this.cancelVisibility();
      this.cancelVisibility = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  initSwiper() {
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper JS is not loaded yet.');
      return;
    }

    const swiperContainer = this.querySelector('.swiper');
    if (!swiperContainer) {
      this.hideParentSectionIfEmpty();
      return;
    }

    const slideCount = swiperContainer.querySelectorAll('.swiper-slide').length;
    if (!slideCount) {
      this.hideParentSectionIfEmpty();
      return;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
    }

    const offsets = window.getWakaSliderOffsets();
    const offsetMobile = offsets.mobile;
    const offsetLg = offsets.lg;
    const offsetXl = offsets.xl;

    this.swiper = new Swiper(swiperContainer, {
      slidesPerView: 1.4,
      spaceBetween: 12,
      slidesOffsetBefore: offsetMobile,
      slidesOffsetAfter: offsetMobile,
      breakpoints: {
        480: {
          slidesPerView: 1.8,
          spaceBetween: 12,
          slidesOffsetBefore: offsetMobile,
          slidesOffsetAfter: offsetMobile,
        },
        640: {
          slidesPerView: 2.2,
          spaceBetween: 12,
          slidesOffsetBefore: offsetMobile,
          slidesOffsetAfter: offsetMobile,
        },
        768: {
          slidesPerView: 2.8,
          spaceBetween: 12,
          slidesOffsetBefore: offsetMobile,
          slidesOffsetAfter: offsetMobile,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 16,
          slidesOffsetBefore: offsetLg,
          slidesOffsetAfter: offsetLg,
        },
        1280: {
          slidesPerView: 4,
          spaceBetween: 16,
          slidesOffsetBefore: offsetXl,
          slidesOffsetAfter: offsetXl,
        },
        1536: {
          slidesPerView: 5,
          spaceBetween: 16,
          slidesOffsetBefore: offsetXl,
          slidesOffsetAfter: offsetXl,
        },
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
    });

    this.toggleControls(slideCount);
  }

  toggleControls(slideCount) {
    const shouldShowControls = slideCount > 1;
    const nextButton = this.querySelector('.swiper-button-next');
    const prevButton = this.querySelector('.swiper-button-prev');

    [nextButton, prevButton].forEach((element) => {
      if (!element) return;

      element.classList.toggle('hidden', !shouldShowControls);
    });

    // Toggle buttons outside the slider in the header
    const section = this.closest('[id^="shopify-section-"]');
    if (section) {
      const headerButtons = section.querySelectorAll(
        '.swiper-button-prev, .swiper-button-next',
      );
      headerButtons.forEach((button) => {
        button.classList.toggle('hidden', !shouldShowControls);
      });
    }
  }

  async loadRecommendations() {
    const productId = this.dataset.productId;
    const maxProducts = parseInt(this.dataset.maxProducts || '15', 10);
    const recommendationsUrl =
      this.dataset.recommendationsUrl || '/recommendations/products';
    const sectionId =
      this.dataset.recommendationsSectionId || 'product-card-recommendations';

    if (!productId) {
      this.renderEmptyState();
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    try {
      const requestUrl = `${recommendationsUrl}?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(maxProducts)}&intent=related`;
      const response = await fetch(requestUrl, {
        headers: {
          Accept: 'text/html',
        },
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to load recommendations: ${response.status}`);
      }

      const markup = (await response.text()).trim();

      if (!markup) {
        this.renderEmptyState();
        return;
      }

      this.renderRecommendations(markup, sectionId);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error(error);
      this.renderEmptyState();
    } finally {
      this.abortController = null;
    }
  }

  renderRecommendations(markup, sectionId) {
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const parser = new DOMParser();
    const parsed = parser.parseFromString(markup, 'text/html');
    const sectionRoot = parsed.getElementById(`shopify-section-${sectionId}`);
    const sourceRoot = sectionRoot || parsed.body;
    const slides = sourceRoot.querySelectorAll('.swiper-slide');

    if (!slides.length) {
      this.renderEmptyState();
      return;
    }

    wrapper.replaceChildren(
      ...Array.from(slides).map((slide) => slide.cloneNode(true)),
    );

    if (!wrapper.querySelector('.swiper-slide')) {
      this.renderEmptyState();
      return;
    }

    this.initSwiperOnNextFrame();
  }

  renderEmptyState() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.innerHTML = `
      <div class="py-16 text-center">
        <p class="text-color-text-light">${this.escapeHtml(this.dataset.emptyText || '')}</p>
      </div>
    `;

    this.hideParentSectionIfEmpty();
  }

  async loadRecentlyViewed() {
    const maxProducts = parseInt(this.dataset.maxProducts || '15', 10);
    const sectionId =
      this.dataset.recentlyViewedSectionId || 'recently-viewed-card';
    const currentProductHandle = this.dataset.currentProductHandle || '';

    const handles = this.getRecentlyViewedHandles()
      .filter((handle) => handle !== currentProductHandle)
      .slice(0, maxProducts);

    if (!handles.length) {
      this.renderEmptyState();
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    try {
      // Fetch all product cards in parallel — Promise.all preserves order (newest first)
      const responses = await Promise.all(
        handles.map((handle) =>
          fetch(
            `/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`,
            {
              headers: { Accept: 'text/html' },
              signal: this.abortController.signal,
            },
          ),
        ),
      );

      const markupList = await Promise.all(
        responses.map(async (response) => {
          if (!response.ok) return null;
          return response.text();
        }),
      );

      const parser = new DOMParser();
      const shopifySectionId = `shopify-section-${sectionId}`;
      const slides = [];

      for (const markup of markupList) {
        if (!markup) continue;
        const parsed = parser.parseFromString(markup, 'text/html');
        const sectionRoot = parsed.getElementById(shopifySectionId);
        const slide = (sectionRoot || parsed.body).querySelector(
          '.swiper-slide',
        );
        if (slide) slides.push(slide.cloneNode(true));
      }

      if (!slides.length) {
        this.renderEmptyState();
        return;
      }

      const wrapper = this.querySelector('.swiper-wrapper');
      if (!wrapper) return;

      wrapper.replaceChildren(...slides);
      this.initSwiperOnNextFrame();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.renderEmptyState();
    } finally {
      this.abortController = null;
    }
  }

  getRecentlyViewedHandles() {
    try {
      const stored = localStorage.getItem('shopify_recently_viewed');
      if (!stored) return [];
      const handles = JSON.parse(stored);
      return Array.isArray(handles) ? handles : [];
    } catch {
      return [];
    }
  }

  hideParentSectionIfEmpty() {
    const section = this.closest('[id^="shopify-section-"]');
    if (section) {
      const sliderBlocks = section.querySelectorAll('products-slider');
      const hasAnySlides = Array.from(sliderBlocks).some(
        (sliderBlock) => sliderBlock.querySelector('.swiper-slide') !== null,
      );

      if (!hasAnySlides) {
        section.style.display = 'none';
      }
      return;
    }

    const productsSlider = this.closest('.products-slider');
    if (productsSlider) {
      productsSlider.style.display = 'none';
    }
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }
}

if (!customElements.get('products-slider')) {
  customElements.define('products-slider', ProductsSlider);
}

class ProductsSliderTabs extends HTMLElement {
  constructor() {
    super();
    // Track per-tab state: whether a lazy tab has been loaded
    this._loadedTabs = new Set();
    // Per-tab AbortControllers keyed by panel id
    this._abortControllers = new Map();
    this._handleTabClick = this._handleTabClick.bind(this);
    this._cancelVisibility = null;
  }

  connectedCallback() {
    // Mark first tab as already loaded (server-rendered)
    const panels = this.querySelectorAll('.products-slider-tabs__panel');
    const firstPanel = panels[0];
    if (firstPanel) {
      this._loadedTabs.add(firstPanel.id);
    }

    this.addEventListener('click', this._handleTabClick);

    // Initialising Swiper measures every slide, so wait until the section is
    // near the viewport. ensureSwiper is a no-op if the slider's own
    // visibility gate got there first.
    this._cancelVisibility = window.wakaWhenVisible(this, () => {
      this._cancelVisibility = null;

      if (firstPanel) {
        const slider = firstPanel.querySelector('products-slider');
        if (slider && typeof slider.ensureSwiper === 'function') {
          slider.ensureSwiper();
        }
      }

      // Wire the outer nav buttons to the active tab's swiper
      this._bindNavButtons();
    });
  }

  disconnectedCallback() {
    if (this._cancelVisibility) {
      this._cancelVisibility();
      this._cancelVisibility = null;
    }

    this.removeEventListener('click', this._handleTabClick);
    for (const ctrl of this._abortControllers.values()) {
      ctrl.abort();
    }
    this._abortControllers.clear();
  }

  _handleTabClick(event) {
    const btn = event.target.closest('[role="tab"]');
    if (!btn) return;

    const panelId = btn.getAttribute('aria-controls');
    if (!panelId) return;

    const activeTabClasses = ['border-primary', 'text-primary', 'font-medium'];
    const inactiveTabClasses = [
      'border-transparent',
      'text-text-muted',
      'hover:border-primary',
      'hover:text-primary',
    ];

    // Deactivate all tabs
    this.querySelectorAll('[role="tab"]').forEach((t) => {
      t.setAttribute('aria-selected', 'false');
      t.classList.remove(...activeTabClasses);
      t.classList.add(...inactiveTabClasses);
    });

    // Activate clicked tab
    btn.setAttribute('aria-selected', 'true');
    btn.classList.remove(...inactiveTabClasses);
    btn.classList.add(...activeTabClasses);

    // Hide all panels, show selected
    this.querySelectorAll('.products-slider-tabs__panel').forEach((panel) => {
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
    });

    const activePanel = this.querySelector(`#${CSS.escape(panelId)}`);
    if (!activePanel) return;

    activePanel.hidden = false;
    activePanel.setAttribute('aria-hidden', 'false');

    // Lazy load if not yet loaded
    if (!this._loadedTabs.has(panelId)) {
      this._lazyLoadPanel(activePanel);
    } else {
      // Ensure swiper is active for this tab
      const slider = activePanel.querySelector('products-slider');
      if (slider && typeof slider.ensureSwiper === 'function') {
        slider.ensureSwiper();
        this._bindNavButtons();
      }
    }
  }

  async _lazyLoadPanel(panel) {
    const panelId = panel.id;
    const source = panel.dataset.source;
    const maxProducts = parseInt(panel.dataset.maxProducts || '15', 10);
    const emptyText = panel.dataset.emptyText || '';

    // Cancel any in-flight request for this panel
    if (this._abortControllers.has(panelId)) {
      this._abortControllers.get(panelId).abort();
    }
    const ctrl = new AbortController();
    this._abortControllers.set(panelId, ctrl);

    const slider = panel.querySelector('products-slider');
    if (!slider) return;

    try {
      let slides = [];

      if (source === 'collection') {
        slides = await this._fetchCollectionSlides(panel, ctrl.signal);
      } else if (source === 'product_list') {
        slides = await this._fetchProductListSlides(panel, ctrl.signal);
      }

      const wrapper = slider.querySelector('.swiper-wrapper');
      if (!wrapper) return;

      if (!slides.length) {
        this._renderPanelEmptyState(slider, emptyText);
        this._loadedTabs.add(panelId);
        return;
      }

      // Trim to max on client side (collection endpoint may return more)
      const trimmed = slides.slice(0, maxProducts);
      wrapper.replaceChildren(...trimmed.map((s) => s.cloneNode(true)));

      if (!wrapper.querySelector('.swiper-slide')) {
        this._renderPanelEmptyState(slider, emptyText);
        this._loadedTabs.add(panelId);
        return;
      }

      this._loadedTabs.add(panelId);

      // Measure the new slides in the next frame, not the one that inserted
      // them. Nav buttons bind in the same callback, after the swiper exists.
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        slider.initSwiper();
        this._bindNavButtons();
      });
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[ProductsSliderTabs] Failed to load tab:', error);
      this._renderPanelEmptyState(slider, emptyText);
      this._loadedTabs.add(panelId);
    } finally {
      this._abortControllers.delete(panelId);
    }
  }

  async _fetchCollectionSlides(panel, signal) {
    const collectionUrl = panel.dataset.collectionUrl;
    const sectionId =
      panel.dataset.collectionSectionId || 'collection-product-cards-swiper';

    if (!collectionUrl) return [];

    const url = `${collectionUrl}?section_id=${encodeURIComponent(sectionId)}`;
    const response = await fetch(url, {
      headers: { Accept: 'text/html' },
      signal,
    });
    if (!response.ok)
      throw new Error(`Collection fetch failed: ${response.status}`);

    const markup = (await response.text()).trim();
    if (!markup) return [];

    const parser = new DOMParser();
    const parsed = parser.parseFromString(markup, 'text/html');
    const sectionRoot = parsed.getElementById(`shopify-section-${sectionId}`);
    const root = sectionRoot || parsed.body;
    return Array.from(root.querySelectorAll('.swiper-slide'));
  }

  async _fetchProductListSlides(panel, signal) {
    const handlesRaw = panel.dataset.productHandles || '';
    const sectionId = panel.dataset.productSectionId || 'product-card-swiper';

    if (!handlesRaw.trim()) return [];

    const handles = handlesRaw
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);

    if (!handles.length) return [];

    const parser = new DOMParser();
    const shopifySectionId = `shopify-section-${sectionId}`;
    const slides = [];

    // Fetch all product pages in parallel; order is preserved by Promise.all
    const responses = await Promise.all(
      handles.map((handle) =>
        fetch(
          `/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`,
          {
            headers: { Accept: 'text/html' },
            signal,
          },
        ),
      ),
    );

    const markupList = await Promise.all(
      responses.map(async (res) => {
        if (!res.ok) return null;
        return res.text();
      }),
    );

    for (const markup of markupList) {
      if (!markup) continue;
      const parsed = parser.parseFromString(markup, 'text/html');
      const sectionRoot = parsed.getElementById(shopifySectionId);
      const slide = (sectionRoot || parsed.body).querySelector('.swiper-slide');
      if (slide) slides.push(slide.cloneNode(true));
    }

    return slides;
  }

  _renderPanelEmptyState(slider, emptyText) {
    if (slider.swiper) {
      slider.swiper.destroy(true, true);
      slider.swiper = null;
    }
    slider.innerHTML = `
      <div class="py-16 text-center">
        <p class="body-sm text-text-muted">${slider.escapeHtml ? slider.escapeHtml(emptyText) : emptyText}</p>
      </div>
    `;
  }

  _bindNavButtons() {
    const activePanel = this.querySelector(
      '.products-slider-tabs__panel:not([hidden])',
    );
    if (!activePanel) return;

    const prevBtn = activePanel.querySelector(
      '.products-slider-tabs__nav-prev',
    );
    const nextBtn = activePanel.querySelector(
      '.products-slider-tabs__nav-next',
    );
    if (!prevBtn || !nextBtn) return;

    const slider = activePanel.querySelector('products-slider');
    if (!slider) return;

    // Update the nav buttons' swiper reference by moving them into the slider's scope
    // The swiper navigation is controlled by the products-slider.initSwiper binding.
    // We achieve outer-button wiring by updating the swiper navigation options if swiper is active.
    if (slider.swiper) {
      if (
        slider.swiper.navigation &&
        typeof slider.swiper.navigation.destroy === 'function'
      ) {
        slider.swiper.navigation.destroy();
      }
      slider.swiper.params.navigation.prevEl = prevBtn;
      slider.swiper.params.navigation.nextEl = nextBtn;
      slider.swiper.navigation.init();
      slider.swiper.navigation.update();

      // Visibility: show buttons only when there are enough slides
      const slideCount = slider.querySelectorAll(
        '.swiper-slide:not(.swiper-slide-duplicate)',
      ).length;
      const show = slideCount > 1;
      prevBtn.classList.toggle('hidden', !show);
      nextBtn.classList.toggle('hidden', !show);
    } else {
      prevBtn.classList.add('hidden');
      nextBtn.classList.add('hidden');
    }
  }
}

if (!customElements.get('products-slider-tabs')) {
  customElements.define('products-slider-tabs', ProductsSliderTabs);
}
